import { Box } from "../../box";
import { NodeType, useCollection, useOnlyCollectionItems, useScrollableCollection } from "../../collection";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { Keys, appendEventKey, cssModule, isEmptyArray, isNil, isNumber, mergeProps, useAutoFocusChild, useControllableState, useDisposables, useEventCallback, useFocusManager, useFocusScope, useId, useKeyedRovingFocus, useMergedRefs, useRefState } from "../../shared";
import { ListboxContext } from "./ListboxContext";
import { ListboxOption } from "./ListboxOption";
import { ListboxSection } from "./ListboxSection";
import { useResponsiveValue } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
export const OptionKeyProp = "data-o-ui-key";
const DefaultElement = "div";

function useCollectionNodes(children, nodes) {
  const collectionNodes = useCollection(children);
  return nodes !== null && nodes !== void 0 ? nodes : collectionNodes;
}

function useSelectionManager(items, {
  selectedKeys
}) {
  return useMemo(() => {
    const toggleKey = key => {
      return selectedKeys.includes(key) ? selectedKeys.filter(x => x !== key) : [...selectedKeys, key];
    };

    const toggleSelection = key => {
      return selectedKeys[0] === key ? [] : [key];
    };

    const extendSelection = toKey => {
      if (selectedKeys.length > 0) {
        const lastKey = selectedKeys[selectedKeys.length - 1];
        const newKeys = new Set(selectedKeys);
        let startIndex = items.findIndex(x => x.key === lastKey);
        let endIndex = items.findIndex(x => x.key === toKey); // Support both directions.

        if (startIndex > endIndex) {
          [startIndex, endIndex] = [endIndex, startIndex];
        }

        for (let i = startIndex; i <= endIndex; i += 1) {
          newKeys.add(items[i].key);
        }

        return Array.from(newKeys);
      }

      return selectedKeys;
    };

    return {
      extendSelection,
      selectedKeys,
      toggleKey,
      toggleSelection
    };
  }, [items, selectedKeys]);
}

export function InnerListbox({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  as = DefaultElement,
  autoFocus,
  children,
  defaultFocusTarget,
  defaultSelectedKeys,
  fluid,
  focusOnHover,
  // TODO: Could it be removed now that useImperativeHandle expose the focus? If yes, also remove from Menu (which might not event need the useImperativeHandle)
  forwardedRef,
  id,
  nodes: nodesProp,
  onFocusChange,
  onSelectionChange,
  selectedKeys: selectedKeysProp,
  selectionMode = "single",
  tabbable = true,
  useVirtualFocus,
  validationState,
  ...rest
}) {
  var _selectionManager$sel;

  const fluidValue = useResponsiveValue(fluid);
  const [selectedKeys, setSelectedKeys] = useControllableState(selectedKeysProp, defaultSelectedKeys, []);
  const [searchQueryRef, setSearchQuery] = useRefState("");
  const [focusScope, setFocusRef] = useFocusScope();
  const containerRef = useMergedRefs(setFocusRef);
  const nodes = useCollectionNodes(children, nodesProp);
  const items = useOnlyCollectionItems(nodes);
  const selectionManager = useSelectionManager(items, {
    selectedKeys
  });
  const focusManager = useFocusManager(focusScope, {
    isVirtual: useVirtualFocus,
    keyProp: OptionKeyProp
  }); // TODO: Would be nice to find a better way to give control over the focused item to the parent.

  useImperativeHandle(forwardedRef, () => {
    const element = containerRef.current;
    element.focusManager = focusManager;
    return element;
  });

  const updateSelectedKeys = (event, newKeys) => {
    if (selectionMode !== "none") {
      setSelectedKeys(newKeys);
    }

    if (!isNil(onSelectionChange)) {
      onSelectionChange(event, newKeys);
    }
  };

  const handleSelectOption = useEventCallback((event, key) => {
    let newKeys;

    if (selectionMode === "multiple") {
      newKeys = selectionManager.toggleKey(key);
    } else {
      newKeys = selectionManager.toggleSelection(key);
    }

    if (useVirtualFocus) {
      focusManager.focusKey(key);
    }

    updateSelectedKeys(event, newKeys);
  });
  const handleFocusOption = useEventCallback((event, key, activeElement) => {
    if (!isNil(onFocusChange)) {
      onFocusChange(event, key, activeElement);
    }
  });
  const searchDisposables = useDisposables();
  const handleKeyDown = useEventCallback(event => {
    searchDisposables.dispose();

    switch (event.key) {
      case Keys.arrowDown:
        {
          event.preventDefault();
          const activeElement = focusManager.focusNext();
          const key = activeElement.getAttribute(OptionKeyProp);

          if (selectionMode === "multiple") {
            if (event.shiftKey) {
              const newKeys = selectionManager.toggleKey(key);
              updateSelectedKeys(event, newKeys);
            }
          }

          if (useVirtualFocus && !isNil(onFocusChange)) {
            onFocusChange(event, key, activeElement);
          }

          break;
        }

      case Keys.arrowUp:
        {
          event.preventDefault();
          const activeElement = focusManager.focusPrevious();
          const key = activeElement.getAttribute(OptionKeyProp);

          if (selectionMode === "multiple") {
            if (event.shiftKey) {
              const newKeys = selectionManager.toggleKey(key);
              updateSelectedKeys(event, newKeys);
            }
          }

          if (useVirtualFocus && !isNil(onFocusChange)) {
            onFocusChange(event, key, activeElement);
          }

          break;
        }

      case Keys.home:
        {
          event.preventDefault();
          const activeElement = focusManager.focusFirst();

          if (useVirtualFocus && !isNil(onFocusChange)) {
            onFocusChange(event, activeElement.getAttribute(OptionKeyProp), activeElement);
          }

          break;
        }

      case Keys.end:
        {
          event.preventDefault();
          const activeElement = focusManager.focusLast();

          if (useVirtualFocus && !isNil(onFocusChange)) {
            onFocusChange(event, activeElement.getAttribute(OptionKeyProp), activeElement);
          }

          break;
        }

      case Keys.enter:
        {
          event.preventDefault();
          handleSelectOption(event, document.activeElement.getAttribute(OptionKeyProp));
          break;
        }

      case Keys.space:
        {
          event.preventDefault();
          const key = document.activeElement.getAttribute(OptionKeyProp);

          if (selectionMode === "single") {
            const newKeys = selectionManager.toggleSelection(key);
            updateSelectedKeys(event, newKeys);
          } else if (selectionMode === "multiple") {
            let newKeys = [];

            if (event.shiftKey) {
              newKeys = selectionManager.extendSelection(key);
            } else {
              newKeys = selectionManager.toggleKey(key);
            }

            updateSelectedKeys(event, newKeys);
          }

          break;
        }
      // eslint-disable-next-line no-fallthrough

      default:
        if (event.key.length === 1) {
          event.preventDefault();
          const query = appendEventKey(searchQueryRef.current, event.key);
          setSearchQuery(query);
          focusManager.search(query); // Clear search query.

          searchDisposables.setTimeout(() => {
            setSearchQuery("");
          }, 350);
        }

    }
  });
  useKeyedRovingFocus(focusScope, selectionManager.selectedKeys[0], {
    isDisabled: !tabbable,
    keyProp: OptionKeyProp
  });
  useAutoFocusChild(focusManager, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus,
    target: (_selectionManager$sel = selectionManager.selectedKeys[0]) !== null && _selectionManager$sel !== void 0 ? _selectionManager$sel : defaultFocusTarget
  });
  const scrollableProps = useScrollableCollection(containerRef, {
    // A listbox have a border-size of 1px
    itemSelector: ".o-ui-listbox-option",
    maxHeight: 12 * 32 + 2 * 1,
    // 32px is the default listbox option height.
    paddingHeight: 2 * 1,
    sectionSelector: ".o-ui-listbox-section"
  });
  const rootId = useId(id, "o-ui-listbox");

  const renderOption = ({
    key,
    index,
    elementType: As = ListboxOption,
    ref,
    content,
    props = {},
    tooltip
  }) => /*#__PURE__*/_jsx(As, { ...mergeProps(props, {
      id: `${rootId}-option-${index + 1}`,
      item: {
        key: key,
        tooltip
      },
      key,
      ref
    }),
    children: content
  });

  const renderSection = ({
    key,
    index,
    elementType: As = ListboxSection,
    ref,
    props = {},
    items: sectionItems
  }) => {
    if (isEmptyArray(sectionItems)) {
      return null;
    }

    return /*#__PURE__*/_jsx(As, { ...mergeProps(props, {
        id: `${rootId}-section-${index + 1}`,
        key,
        ref
      }),
      children: sectionItems.map(x => renderOption(x))
    });
  };

  const activeDescendant = useVirtualFocus ? focusManager.getActiveElement() : null;
  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      "aria-activedescendant": !isNil(activeDescendant) ? activeDescendant.getAttribute("id") : undefined,
      "aria-invalid": validationState === "invalid" ? true : undefined,
      "aria-label": ariaLabel,
      "aria-labelledby": isNil(ariaLabel) ? ariaLabelledBy : undefined,
      "aria-multiselectable": selectionMode === "multiple" ? true : undefined,
      as,
      className: cssModule("o-ui-listbox", fluidValue && "fluid", validationState),
      id: rootId,
      onKeyDown: handleKeyDown,
      ref: containerRef,
      role: "listbox"
    }, scrollableProps),
    children: /*#__PURE__*/_jsx(ListboxContext.Provider, {
      value: {
        focusManager,
        focusOnHover,
        onFocus: handleFocusOption,
        onSelect: handleSelectOption,
        selectedKeys: selectionManager.selectedKeys
      },
      children: nodes.map(node => {
        switch (node.type) {
          case NodeType.item:
            return renderOption(node);

          case NodeType.section:
            return renderSection(node);

          default:
            return null;
        }
      })
    })
  });
}
InnerListbox.defaultElement = DefaultElement;
export const Listbox = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerListbox, { ...props,
  forwardedRef: ref
}));